import {
    GridRenderCellParams,
    GridValueFormatterParams,
    GridValueGetterParams,
} from '@mui/x-data-grid';
import { DataTableProps } from 'components/data/DataTable/DataTable';
import { Sparkline } from 'components/data/Sparklines/Sparkline';
import DeviceBatteryIcon from 'components/Devices/DeviceBatteryIcon/DeviceBatteryIcon';
import DeviceHealthIcon from 'components/Devices/DeviceHealthIcon/DeviceHealthIcon';
import { getMonthDayYear } from 'helpers/dates';
import { getBatteryLevel, getObjectValueByPath } from 'helpers/general';
import { Device, DeviceHealth, DeviceLastReading } from 'types';
import { TankLevelIndicator } from 'components/data/TankLevelIndicator/TankLevelIndicator';
import { deviceHealthFormatter } from 'components/DataGridCells/data-grid-formatters';
import { deviceHealthSorter } from 'components/DataGridCells/data-grid-sorters';
import { formatDistanceToNowStrict } from 'date-fns';
import { Tooltip, Typography } from '@mui/material';
import {
    formatDecimalToFixedNumber,
    formatDecimalToFixedPercent,
} from 'helpers';
import { convertToDisplayName } from 'components/form/helpers';
import DeviceSignalIcon from 'components/Devices/DeviceSignalIcon/DeviceSignalIcon';
import { DeviceUtilCell } from 'components/DataGridCells/DeviceUtilCell/DeviceUtilCell';
import DeviceStatus from 'components/Devices/DeviceStatus/DeviceStatus';
import { getBackOfficeSyncIcon } from 'store/services/third-party-backoffice';
import { consumptionTypeIcons, consumptionTypeLabels } from 'lib/constants';
import { GroupSelector } from 'views/LiveMonitoring/components/GroupSelector/GroupSelector';
import { DeviceNextDeliveryCell } from 'components/DataGridCells/DeviceNextDeliveryCell/DeviceNextDeliveryCell';
import DaysUntilRecommendedFill from 'components/data/DaysUntilRecommendedFill/DaysUntilRecommendedFill';
import { DeviceBackOfficeSyncIcon } from 'components/Devices/DeviceBackOfficeSyncIcon/DeviceBackOfficeSyncIcon';
import { HelpOutline } from '@mui/icons-material';

interface GetDeviceColumnsProps {
    refetchListView: React.Dispatch<React.SetStateAction<boolean>>;
}

/*
 * Returns the columns for the device data grid
 *  Accepts a refetch function to be passed to the DeviceNextDeliveryCell component
 */
export function getDeviceColumns({ refetchListView }: GetDeviceColumnsProps) {
    const deviceColumns: DataTableProps['columns'] = [
        {
            headerName: 'Next Delivery',
            hideByDefault: true,
            field: 'nextDelivery',
            fbKey: 'next_delivery',
            flex: 1,
            align: 'center', // align the text in the center
            sortable: false, // has to be calculated client-side
            valueGetter: (params: GridValueGetterParams) => {
                const lastActiveDelivery = getObjectValueByPath(
                    params.row,
                    'lastActiveDelivery'
                );

                const device = params.row;

                // If the 'lastActiveDelivery' field is not null, then there is ticket data
                if (lastActiveDelivery) {
                    // grab ticket status
                    const ticketDirectory = getObjectValueByPath(
                        lastActiveDelivery,
                        'ticketDirectory'
                    );

                    // if there is an active delivery, lets show the delivery date
                    if (ticketDirectory === 'active') {
                        const date = lastActiveDelivery.plannedDate
                            ? lastActiveDelivery.plannedDate
                            : device.duf_7;

                        if (date) {
                            const ms_timestamp = new Date(date).getTime();
                            const formattedDate = getMonthDayYear(ms_timestamp);
                            return formattedDate;
                        } else {
                            // Fallback for when grabbing a date doesn't work out
                            return 'View Ticket';
                        }
                    }
                }

                // else we made it this far, there is no active delivery, so return null
                return null;
            },
            renderCell: (params: GridRenderCellParams) => {
                const device: Device = params.row;
                const deliveryDate: string = params.value;
                return (
                    <DeviceNextDeliveryCell
                        device={device}
                        deliveryDate={deliveryDate}
                        refetchListView={refetchListView}
                    />
                );
            },
        },
        {
            headerName: 'Device Health',
            field: 'health',
            fbKey: 'device_health',
            flex: 1,
            renderCell: (params: GridRenderCellParams<DeviceHealth>) => {
                return <DeviceHealthIcon {...params.value} />;
            },
            sortComparator: deviceHealthSorter,
            valueFormatter: (params: GridValueFormatterParams) =>
                deviceHealthFormatter(params),
        },
        {
            headerName: 'Tank Level',
            renderHeader: () => (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <strong>Tank Level</strong>
                    <Tooltip
                        title="Tank level based on the most recent monitor reading."
                        enterTouchDelay={0}
                        arrow
                        sx={{ marginLeft: '5px' }}
                    >
                        <HelpOutline color="action" fontSize="small" />
                    </Tooltip>
                </span>
            ),
            field: 'lastReading.tank',
            fbKey: 'tank',
            flex: 1,
            renderCell: (
                params: GridRenderCellParams<{
                    gallons: Device['gallons'];
                    tankLevelPercentage: DeviceLastReading['tank'];
                }>
            ) => {
                const gallons = getObjectValueByPath(params.row, 'gallons');
                const tankLevelPercentage = getObjectValueByPath(
                    params.row,
                    'lastReading.tank'
                );
                return (
                    <TankLevelIndicator
                        tankLevelPercentage={tankLevelPercentage}
                        gallons={gallons}
                    />
                );
            },
            valueFormatter: (params: GridValueFormatterParams) => {
                const percentage = params.value;
                if (percentage) {
                    return `${percentage.toFixed(2)}%`;
                }

                return '';
            },
        },
        {
            headerName: 'Customer Name',
            field: 'customer_name',
            valueGetter: (params: GridValueGetterParams) => {
                if (params.value) return params.value;
                return `Device ID: ${params.row.shortDeviceId}`;
            },
        },
        {
            headerName: 'Tank Name',
            field: 'name',
        },
        {
            headerName: 'Address',
            field: 'address',
            valueGetter: (params: GridValueGetterParams) => {
                if (params.value) return params.value;
                return 'No Address Entered';
            },
        },
        {
            headerName: 'Short Device Id',
            field: 'shortDeviceId',
            flex: 1,
        },
        {
            headerName: 'Last Reading Date',
            field: 'lastReading.time',
            fbKey: 'last_reading_time',
            valueGetter: (params: GridValueGetterParams) => {
                const time = getObjectValueByPath(
                    params.row,
                    'lastReading.time'
                );

                return typeof time === 'number'
                    ? formatDistanceToNowStrict(time)
                    : null;
            },
        },
        {
            headerName: '30 Day Sparkline',
            field: 'l30',
            disableExport: true,
            renderCell: (params: GridRenderCellParams) => {
                const data = params.value ? params.value.split(',') : [];
                return <Sparkline data={data} />;
            },
        },
        {
            headerName: 'Battery',
            field: 'lastReading',
            fbKey: 'battery',
            sortable: false,
            sortComparator: (v: DeviceLastReading, v2: DeviceLastReading) => {
                if (v.battery && !v2.battery) {
                    return 1;
                } else if (!v.battery && v2.battery) {
                    return -1;
                } else if (v.battery && v2.battery) {
                    return v.battery > v2.battery ? -1 : 1;
                }
                return 0;
            },
            renderCell: (params) => {
                return (
                    <DeviceBatteryIcon
                        battery={params.row}
                        itemNumber={params.row.item_number}
                    />
                );
            },
            valueGetter: (
                params: GridValueFormatterParams<DeviceLastReading>
            ) => {
                if (params.value === undefined) return 0;
                const { battery_crit, battery_warn } = params.value;

                const batteryLevel = getBatteryLevel(
                    battery_crit,
                    battery_warn
                );

                return batteryLevel === 'unknown' ? 0 : batteryLevel;
            },
        },
        {
            headerName: 'Device Id',
            field: 'deviceId',
            hideByDefault: true,
            flex: 1,
        },
        {
            headerName: 'Account Id',
            field: 'accountId',
            hideByDefault: true,
        },
        {
            headerName: 'Deployment Date',
            field: 'deployed_time',
            hideByDefault: true,
            valueFormatter(params: GridValueFormatterParams) {
                return params.value ? getMonthDayYear(params.value) : null;
            },
        },
        {
            headerName: 'Estimated Tank Level',
            renderHeader: () => (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <strong>Est. Tank Level</strong>
                    <Tooltip
                        title="Tank level estimated based on prediction algorithms."
                        enterTouchDelay={0}
                        arrow
                        sx={{ marginLeft: '5px', marginRight: '10px' }}
                    >
                        <HelpOutline color="action" fontSize="small" />
                    </Tooltip>
                </span>
            ),
            field: 'estimated_tank',
            hideByDefault: true,
            flex: 1,
            minWidth: 200,
            sortable: false, // calculated using predictions, which are too dynamic for ES to handle without rework
            renderCell: (
                params: GridRenderCellParams<{
                    gallons: DeviceLastReading['gallons'];
                    tankLevelPercentage: DeviceLastReading['tank'];
                }>
            ) => {
                const gallons = getObjectValueByPath(
                    params.row,
                    'lastReading.gallons'
                );
                const tankLevelPercentage = getObjectValueByPath(
                    params.row,
                    'lastReading.tank'
                );
                return (
                    <TankLevelIndicator
                        tankLevelPercentage={tankLevelPercentage}
                        gallons={gallons}
                    />
                );
            },
            valueGetter: (params: GridValueGetterParams) =>
                params.row?.lastReading, // needed so valueFormatter has data to work with
            valueFormatter: (params: GridValueFormatterParams) => {
                const lastReading = params.value;

                if (lastReading?.predictions) {
                    const today = new Date();
                    const todayDay =
                        today.getDate() < 10
                            ? `0${today.getDate()}`
                            : today.getDate();
                    const todayDd = `${today.getFullYear()}${
                        today.getMonth() + 1
                    }${todayDay}`;

                    if (lastReading.predictions[todayDd]) {
                        if (lastReading.predictions[todayDd].tank >= 0) {
                            return `${lastReading.predictions[todayDd].tank}%`;
                        } else if (
                            lastReading.predictions[todayDd].tankCAvg7 >= 0
                        ) {
                            return `${lastReading.predictions[todayDd].tankCAvg7}%`;
                        }
                    }
                }

                if (lastReading.tank) {
                    return `${lastReading.tank.toFixed(2)}%`;
                }

                return '';
            },
        },
        {
            headerName: 'Gallons',
            field: 'gallons',
            hideByDefault: true,
            valueFormatter(params) {
                return formatDecimalToFixedNumber(params.value);
            },
        },
        {
            headerName: 'Ullage',
            field: 'ullage',
            hideByDefault: true,
            valueFormatter(params) {
                return formatDecimalToFixedNumber(params.value);
            },
        },
        {
            headerName: 'Fuel Type',
            field: 'fuelType',
            hideByDefault: true,
        },
        {
            headerName: 'Capacity',
            field: 'capacity',
            hideByDefault: true,
        },
        {
            headerName: 'Orientation',
            field: 'orientation',
            hideByDefault: true,
        },
        {
            headerName: 'Status',
            field: 'status',
            hideByDefault: true,
            renderCell: (params) => {
                return <DeviceStatus status={params.value} />;
            },
        },
        {
            headerName: 'Group',
            field: 'group',
            hideByDefault: true,
            minWidth: 250,
            renderCell: (params) => {
                return <GroupSelector device={params.row} />;
            },
        },
        {
            headerName: 'Delivery Type',
            field: 'delivery_type',
            hideByDefault: true,
        },
        {
            headerName: 'Connection Type',
            field: 'connection_type',
            hideByDefault: true,
        },
        {
            headerName: 'Carrier',
            field: 'carrier',
            hideByDefault: true,
        },
        {
            headerName: 'Platform',
            field: 'platform',
            hideByDefault: true,
        },
        {
            headerName: 'Mfg Tla',
            field: 'mfg_tla',
            hideByDefault: true,
        },
        {
            headerName: 'Mfg Tla Rev',
            field: 'mfg_tla_rev',
            hideByDefault: true,
        },
        {
            headerName: 'Mfg Tla Dev',
            field: 'mfg_tla_dev',
            hideByDefault: true,
        },
        {
            headerName: 'Item Number',
            field: 'item_number',
            hideByDefault: true,
        },
        {
            headerName: 'Product Id',
            field: 'product_id',
            hideByDefault: true,
        },
        {
            headerName: 'Product Name',
            field: 'product_name',
            hideByDefault: true,
        },
        {
            headerName: 'Hw Rev',
            field: 'hw_rev',
            hideByDefault: true,
        },
        {
            headerName: 'Software Revision',
            field: 'sw_rev',
            hideByDefault: true,
        },
        {
            headerName: 'Notes',
            field: 'notes',
            hideByDefault: true,
        },
        {
            headerName: 'R Int',
            field: 'r_int',
            hideByDefault: true,
        },
        {
            headerName: 'T Int',
            field: 't_int',
            hideByDefault: true,
        },
        {
            headerName: 'Last 15 Temp',
            field: 't_15',
            hideByDefault: true,
            sortable: false,
            renderCell: (params) => {
                const data = params.value ? params.value.split(',') : [];
                return <Sparkline data={data} />;
            },
        },
        {
            headerName: 'Last 15 Voltage',
            field: 'v_15',
            hideByDefault: true,
            sortable: false,
            renderCell: (params) => {
                const data = params.value ? params.value.split(',') : [];
                return <Sparkline data={data} />;
            },
        },
        {
            headerName: 'Avg. Daily Usage (gal)',
            field: 'c_avg_7',
            fbKey: 'c_avg',
            hideByDefault: true,
            valueFormatter(params) {
                return formatDecimalToFixedNumber(params.value);
            },
        },
        {
            headerName: 'Avg. Daily Usage (%)',
            field: 'p_avg_7',
            fbKey: 'p_avg',
            hideByDefault: true,
            valueFormatter(params) {
                return formatDecimalToFixedPercent(params.value);
            },
        },
        {
            headerName: 'Standard Deviation',
            field: 'std_dev',
            hideByDefault: true,
        },
        {
            headerName: 'Days Until Rec. Fill',
            field: 'duf_7',
            hideByDefault: false,
            renderCell: (params) => {
                return <DaysUntilRecommendedFill duf_7={params.value} />;
            },
        },
        {
            headerName: 'Est. Delivery Date',
            field: 'est_delivery_date',
            hideByDefault: false,
            valueGetter(params) {
                return params.row.duf_7
                    ? getMonthDayYear(params.row.duf_7)
                    : '';
            },
        },
        {
            headerName: 'Usage Type',
            field: 'consumptionType',
            hideByDefault: true,
            sortable: false,
            valueFormatter: (
                params: GridValueFormatterParams<Device['consumptionType']>
            ) => {
                const listedConsumptionTypes = params.value
                    ? Object.entries(params.value)
                          .filter((e) => e[1] === true)
                          .map((e) => convertToDisplayName(e[0]))
                          .join(', ')
                    : null;
                return listedConsumptionTypes;
            },
            renderCell: (params) => {
                return (
                    <span>
                        {Object.entries(params.value)
                            .filter((e) => e[1] === true)
                            .map((e) => {
                                return (
                                    <Tooltip
                                        title={consumptionTypeLabels[e[0]]}
                                        key={e[0]}
                                    >
                                        {consumptionTypeIcons[e[0]]}
                                    </Tooltip>
                                );
                            })}
                    </span>
                );
            },
        },
        {
            headerName: 'Clone Time',
            field: 'clone_time',
            hideByDefault: true,
            valueFormatter(params) {
                return params.value ? getMonthDayYear(params.value) : null;
            },
        },
        {
            headerName: 'Battery Crit',
            field: 'battery_crit',
            hideByDefault: true,
            renderCell(params: GridRenderCellParams) {
                const batteryFloat = formatDecimalToFixedNumber(params.value);

                const color = !batteryFloat
                    ? undefined
                    : parseInt(batteryFloat) > 0
                    ? 'green'
                    : 'red';

                return (
                    <Typography variant="subtitle2" color={color}>
                        {batteryFloat}
                    </Typography>
                );
            },
        },
        {
            headerName: 'Battery Warn',
            field: 'battery_warn',

            hideByDefault: true,
            renderCell(params: GridRenderCellParams) {
                const batteryFloat = formatDecimalToFixedNumber(params.value);

                const color = !batteryFloat
                    ? undefined
                    : parseInt(batteryFloat) > 0
                    ? 'green'
                    : 'red';

                return (
                    <Typography variant="subtitle2" color={color}>
                        {batteryFloat}
                    </Typography>
                );
            },
        },
        {
            headerName: 'Signal',
            field: 'signal',
            hideByDefault: true,
            sortable: false,
            valueGetter: (params: GridValueGetterParams) => {
                const device = params.row as Device;

                if (device.connection_type === 'wifi') {
                    return device.lastReading?.rssi;
                } else if (device.connection_type === 'cell') {
                    const rsrp = device.rsrp_avg ?? device.lastReading?.rsrp;
                    return rsrp;
                }
            },
            renderCell: (params: GridRenderCellParams) => {
                const device = params.row as Device;
                return <DeviceSignalIcon {...device} />;
            },
        },
        {
            /** @note this field is used here for key syncing purposes for react. That actual field does not serve any purpose data-wise for this column */
            headerName: 'Back Office',
            field: 'backoffice',
            sortable: false,
            hideByDefault: true,
            renderCell(params) {
                const device = params.row;

                return (
                    <DeviceBackOfficeSyncIcon
                        iconConfig={getBackOfficeSyncIcon(device)}
                    />
                );
            },
        },
        {
            headerName: 'Virtual Supplier',
            field: 'virtual_supplier',
            sortable: false,
            hideByDefault: true,
            valueFormatter(params: GridValueFormatterParams) {
                return params.value ? params.value.join('') : null;
            },
        },
        {
            headerName: 'Util',
            field: 'util',
            hideByDefault: true,
            sortable: false,
            disableExport: true,
            renderCell: (params: GridRenderCellParams<any, Device, any>) => {
                const device = params.row;

                return <DeviceUtilCell device={device} />;
            },
        },
    ];

    return deviceColumns;
}
